<template>
  <div class="container card-container">
    <!-- NOTE: General Scheduling Settig -->
    <div
      v-if="pageType !== 'scheduling'"
      class="card md-12"
    >
      <div class="card-body">
        <h5 class="card-title">
          {{ $t("SmartScheduling.Common.Label.GeneralSetting") }}
        </h5>
        <div class="card-text">
          <div class="form-row">
            <div class="col-md-4">
              <div class="form-group">
                <label for="customer-name">
                  {{ $t("SmartScheduling.Common.Data.CustomerName") }}
                </label>
                <input
                  type="text"
                  id="customer-name"
                  v-model="editData.customerName"
                  class="form-control form-control-sm"
                  :placeholder="$t('SmartScheduling.Common.Data.CustomerName')"
                >
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label for="select-language">
                  {{ $t("SmartScheduling.Common.Label.SelectLanguage") }}
                </label>

                <VSelectbox
                  :items="langItems"
                  select-id="select-language"
                  size="sm"
                  block
                  @selectedData="editData.defaultLang = $event"
                  :placeholder="$t('SmartScheduling.Common.Data.Language')"
                />
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label for="select-timezone">
                  {{ $t("SmartScheduling.Common.Label.SelectTimezone") }}
                </label>
                <input
                  type="text"
                  id="select-timezone"
                  v-model="editData.timezone"
                  class="form-control form-control-sm"
                  autocomplete="on"
                  list="select-timezone-list"
                  :placeholder="$t('SmartScheduling.Common.Data.Timezone')"
                >
                <datalist id="select-timezone-list">
                  <option
                    v-for="timezone in timezoneItems"
                    :key="timezone.key"
                    :value="timezone.text"
                  />
                </datalist>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- NOTE: ExclusionWords Setting -->
    <div
      v-if="!isNewUser"
      class="card md-12"
    >
      <div class="card-body">
        <h5 class="card-title">
          {{ $t("SmartScheduling.Common.Label.ExclusionWords") }}
        </h5>
        <p class="card-description">
          {{ $t("SmartScheduling.Common.Label.ExclusionWordsDetail") }}
        </p>
        <div class="card-text">
          <div class="form-row">
            <div class="col-md-2 align-self-center">
              <label class="sub-label">
                {{ $t("SmartScheduling.Common.Label.AllDay") }}
              </label>
            </div>
            <div class="col-md-5">
              <div class="form-group">
                <label for="all-day-empty">
                  {{ $t("SmartScheduling.Common.Label.Empty") }}
                </label>
                <small class="form-text">
                  {{ $t("SmartScheduling.Common.Label.EmptyDetail") }}
                </small>
                <input
                  type="text"
                  id="all-day-empty"
                  v-model="editData.dayPlanOk"
                  class="form-control form-control-sm"
                  :placeholder="$t('SmartScheduling.Common.Label.EmptyPlaceholder')"
                >
              </div>
            </div>
            <div class="col-md-5">
              <div class="form-group">
                <label for="all-day-planned">
                  {{ $t("SmartScheduling.Common.Label.Planned") }}
                </label>
                <small class="form-text">
                  {{ $t("SmartScheduling.Common.Label.PlannedDetail") }}
                </small>
                <input
                  type="text"
                  id="all-day-planned"
                  v-model="editData.dayPlanNg"
                  class="form-control form-control-sm"
                  :placeholder="$t('SmartScheduling.Common.Label.PlannedPlaceholder')"
                >
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="col-md-2 align-self-center">
              <label class="sub-label">
                {{ $t("SmartScheduling.Common.Label.SpecificTime") }}
              </label>
            </div>
            <div class="col-md-5">
              <div class="form-group">
                <label for="pecific-time-empty">
                  {{ $t("SmartScheduling.Common.Label.Empty") }}
                </label>
                <small class="form-text">
                  {{ $t("SmartScheduling.Common.Label.EmptyDetail") }}
                </small>
                <input
                  type="text"
                  id="pecific-time-empty"
                  v-model="editData.timePlanOk"
                  class="form-control form-control-sm"
                  :placeholder="$t('SmartScheduling.Common.Label.EmptyPlaceholder')"
                >
              </div>
            </div>
            <div class="col-md-5">
              <div class="form-group">
                <label for="pecific-time-planned">
                  {{ $t("SmartScheduling.Common.Label.Planned") }}
                </label>
                <small class="form-text">
                  {{ $t("SmartScheduling.Common.Label.PlannedDetail") }}
                </small>
                <input
                  type="text"
                  id="pecific-time-planned"
                  v-model="editData.timePlanNg"
                  class="form-control form-control-sm"
                  :placeholder="$t('SmartScheduling.Common.Label.PlannedPlaceholder')"
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- NOTE: Work Hour Setting -->
    <div class="card md-12">
      <div class="card-body">
        <h5 class="card-title">
          {{ $t("SmartScheduling.Common.Label.WorkHoursSetting") }}
        </h5>
        <div class="card-text">
          <div class="form-row">
            <label class="col-md-5 offset-md-2 sub-label">
              {{ $t("SmartScheduling.Common.Label.WorkHours") }}
            </label>
            <label class="col-md-5 sub-label">
              {{ $t("SmartScheduling.Common.Label.BreakTimes") }}
            </label>
          </div>
          <template v-for="(day, index) in weekdays">
            <div
              class="form-row small-padding"
              :key="index"
            >
              <div class="col-md-2 align-self-center">
                <div class="custom-control custom-switch">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    v-model="editData.workHourSetting[day].isWorkday"
                    :id="day"
                  >
                  <label
                    class="custom-control-label"
                    :for="day"
                  >
                    {{ getWeekdayText(day) }}
                  </label>
                </div>
              </div>

              <div class="col-md-4 form-row small-padding">
                <!-- NOTE: 業務時間 -->
                <template v-if="editData.workHourSetting[day].isWorkday">
                  <div class="col align-self-center small-padding">
                    <VTimeInput v-model="editData.workHourSetting[day].workHour.start" />
                  </div>
                  <div class="col-sm-1 align-self-center align-items-center small-padding">
                    <i
                      class="fa fa-minus"
                      aria-hidden="true"
                    />
                  </div>
                  <div class="col align-self-center small-padding">
                    <VTimeInput v-model="editData.workHourSetting[day].workHour.end" />
                  </div>
                </template>
              </div>

              <div class="col-md-1 align-self-center form-row small-padding">
                <!-- NOTE: Copy to Friday -->
                <VTooltip v-if="index === 0">
                  <template #contents>
                    <button
                      type="button"
                      class="btn btn-borderless-primary"
                      @click="copyToWorkHour"
                    >
                      <i
                        class="fa fa-clone"
                        aria-hidden="true"
                      />
                    </button>
                  </template>
                  <template #tooltip>
                    {{ $t("SmartScheduling.Common.Label.CopyWorkHoursToActiveDay") }}
                  </template>
                </VTooltip>
              </div>

              <div class="col-md-4 form-row small-padding">
                <!-- NOTE: 休憩時間 -->
                <template v-for="(breakTime, i) in editData.workHourSetting[day].breakTime">
                  <div
                    class="form-row small-padding"
                    :key="i"
                  >
                    <template v-if="editData.workHourSetting[day].isWorkday">
                      <div class="col small-padding">
                        <VTimeInput v-model="breakTime.start" />
                      </div>
                      <div class="col-sm-1 align-self-center align-items-center small-padding">
                        <i
                          class="fa fa-minus"
                          aria-hidden="true"
                        />
                      </div>
                      <div class="col small-padding">
                        <VTimeInput v-model="breakTime.end" />
                      </div>
                    </template>
                  </div>
                </template>
              </div>

              <div class="col-md-1 align-self-center form-row small-padding">
                <!-- NOTE: Copy to Friday -->
                <VTooltip v-if="index === 0">
                  <template #contents>
                    <button
                      type="button"
                      class="btn btn-borderless-primary"
                      @click="copyToBreakTime"
                    >
                      <i
                        class="fa fa-clone"
                        aria-hidden="true"
                      />
                    </button>
                  </template>
                  <template #tooltip>
                    {{ $t("SmartScheduling.Common.Label.CopyBreakTimesToActiveDay") }}
                  </template>
                </VTooltip>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VSelectbox from '@/components/VSelectbox.vue'
import VTimeInput from '@/components/VTimeInput.vue'
import VTooltip from '@/components/VTooltip.vue'
import { TIMEZONE_LIST } from '@/config/constants'
import { mapState } from 'vuex'

const WeekdayTable = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
}

export default {
  components: {
    VTimeInput,
    VSelectbox,
    VTooltip
  },
  props: {
    commonSettingData: {
      type: Object,
      default: () => ({})
    },
    pageType: {
      type: String,
      default: 'scheduling'
    }
  },
  data: () => ({
    editData: {
      workHourSetting: {
        monday: Object.assign({}, {
          breakTime: [{ start: '', end: '' }],
          isWorkday: true,
          workHour: { start: '', end: '' }
        }),
        tuesday: Object.assign({}, {
          breakTime: [{ start: '', end: '' }],
          isWorkday: true,
          workHour: { start: '', end: '' }
        }),
        wednesday: Object.assign({}, {
          breakTime: [{ start: '', end: '' }],
          isWorkday: true,
          workHour: { start: '', end: '' }
        }),
        thursday: Object.assign({}, {
          breakTime: [{ start: '', end: '' }],
          isWorkday: true,
          workHour: { start: '', end: '' }
        }),
        friday: Object.assign({}, {
          breakTime: [{ start: '', end: '' }],
          isWorkday: true,
          workHour: { start: '', end: '' }
        }),
        saturday: Object.assign({}, {
          breakTime: [{ start: '', end: '' }],
          isWorkday: false,
          workHour: { start: '', end: '' }
        }),
        sunday: Object.assign({}, {
          breakTime: [{ start: '', end: '' }],
          isWorkday: false,
          workHour: { start: '', end: '' }
        })
      }
    },
    timezoneItems: [].concat(TIMEZONE_LIST),
    hoverFlag: false
  }),
  computed: {
    ...mapState('auth', [
      'isNewUser'
    ]),
    langItems() {
      if (!this.editData) return []
      const lang = this.editData.defaultLang
      return [{
        text: '日本語',
        value: 'ja',
        selected: lang === 'ja'
      },{
        text: 'English',
        value: 'en',
        selected: lang === 'en'
      }]
    },
    weekdays() {
      return Object.keys(WeekdayTable)
    }
  },
  watch: {
    commonSettingData: {
      handler: function(val) {
        this.editData = Object.assign({}, val)
      },
      deep: true
    },
    editData: {
      handler: function (val, old) {
        this.updateEditData(val, old)
      },
      deep: true
    }
  },
  methods: {
    getWeekdayText(key) {
      return this.$t(`SmartScheduling.Common.Weekday.${WeekdayTable[key]}`)
    },
    updateEditData(val, old) {
      if (Object.keys(old).length < 2) return {}
      this.$emit('updateEditData', val)
    },
    copyToWorkHour() {
      const target = Object.keys(WeekdayTable).find((arr) => {
        return this.editData.workHourSetting[arr].isWorkday
      })
      const targetObj = Object.assign({}, this.editData.workHourSetting[target].workHour)
      return Object.keys(WeekdayTable).filter((arr) => {
        return this.editData.workHourSetting[arr].isWorkday
      }).forEach((v) => {
        this.editData.workHourSetting[v].workHour = Object.assign({}, targetObj)
      })
    },
    copyToBreakTime() {
      const target = Object.keys(WeekdayTable).find((arr) => {
        return this.editData.workHourSetting[arr].isWorkday
      })
      return Object.keys(WeekdayTable).filter((arr) => {
        return this.editData.workHourSetting[arr].isWorkday
      }).forEach((v) => {
        this.editData.workHourSetting[v].breakTime = this.editData.workHourSetting[target].breakTime.map((v) => ({
          start: v.start,
          end: v.end
        }))
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@abeja-inc/abeja-ui-library/scss/_variables.scss";

.sub-label {
  font-weight: $font-weight-semibold;
}

.small-padding {
  padding: 4px 0;
}
</style>
